import React from 'react';

import { useChildComponent } from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import {
  useScreenState,
  useScreenDispatch,
  useScreenGo,
  useScreenTitle,
  ScreenLink,
} from '@gogame/screens';

import {
  IntegrationFormDuplicate,
  IntegrationFormEdit,
} from 'scenes/Integrations/components/IntegrationForm';
import { IntegrationActions } from 'scenes/Integrations/components/Actions';
import {
  COLUMN_ID,
  COLUMN_STATUS,
  COLUMN_TYPE,
  COLUMN_BUSINESS_PARTNERS,
  COLUMN_AFFILIATES,
  COLUMN_REG_PLATFORMS,
  COLUMN_EVENT,
  COLUMN_APPLICATIONS,
  COLUMN_CREATED_BY,
  COLUMN_LAST_UPDATED,
  COLUMN_CREATED,
  COLUMN_ACTIVITY_TYPE,
  COLUMN_TRAFFIC_TYPE,
} from 'scenes/Integrations/constants';
import {
  useLoadIntegrationsGrid,
  useIsUpdating,
} from 'scenes/Integrations/data';
import {
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
  SET_TABLE_SETTINGS,
} from 'scenes/Integrations/screenReducer';
import { getConfigurationTypeId } from 'scenes/Integrations/utils';

import IntegrationsToolbar from './Toolbar';

import IntegrationsBulkActions from './IntegrationsBulkActions';

import { getColor } from '../Status';
import { integrationDetailsLink } from '../../navigation';

const IntegrationDetailsLink = ({ value, row }) => {
  return (
    <ScreenLink
      link={integrationDetailsLink}
      pathParams={{
        id: value,
        type: getConfigurationTypeId(row),
      }}
    >
      {value}
    </ScreenLink>
  );
};

const IntegrationDataTable = () => {
  const [childComponent, openChildComponent] = useChildComponent();
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const [screenTitle] = useScreenTitle();
  const go = useScreenGo();
  const apiRef = React.useRef(null);

  const isUpdating = useIsUpdating();

  const { data, isLoading, isRefetching } = useLoadIntegrationsGrid({
    filters: screenState.filters,
    searchQuery: screenState.searchQuery,
    tableSettings: screenState.tableSettings,
  });

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleDuplicate = (data) => {
    openChildComponent(IntegrationFormDuplicate, {
      type: getConfigurationTypeId(data),
      id: data.id,
      onSubmit: ({ typeId, id }) => {
        go(`${typeId}/${id}`);
      },
    });
  };

  const handleEdit = (data) => {
    openChildComponent(IntegrationFormEdit, {
      type: getConfigurationTypeId(data),
      id: data.id,
    });
  };

  const selectedRowsToolbarActions = () => {
    return <IntegrationsBulkActions />;
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching || isUpdating}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        apiRef={apiRef}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        getRowId={getIntegrationRowId}
        toolbar={<IntegrationsToolbar />}
        storageKey={'integrations'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
        checkboxSelection={true}
        selectedRowsToolbarActions={selectedRowsToolbarActions}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'link'}
          headerName={'ID'}
          align={'right'}
          headerAlign={'right'}
          width={80}
          fixed={true}
          resizable={false}
          linkComponent={IntegrationDetailsLink}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Status'}
          type={'status'}
          width={100}
          getStatusColor={getColor}
        />
        <DataGrid.Column
          field={COLUMN_TYPE}
          type={'text'}
          headerName={'Type'}
          width={150}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_BUSINESS_PARTNERS}
          type={'commaList'}
          headerName={'Business Partners'}
          width={150}
          limit={1}
        />
        <DataGrid.Column
          field={COLUMN_AFFILIATES}
          type={'commaList'}
          headerName={'Affiliates'}
          width={260}
          placeholder={'All'}
          limit={1}
        />
        <DataGrid.Column
          field={COLUMN_REG_PLATFORMS}
          type={'commaList'}
          headerName={'Reg Platforms'}
          width={140}
          getItemValue={(opt) => opt?.name || '-'}
          getTooltip={(opt) => opt?.name}
          placeholder={'All'}
          limit={1}
        />

        <DataGrid.Column
          field={COLUMN_EVENT}
          type={'text'}
          headerName={'Event'}
          width={260}
          getValue={(v) => v?.name}
        />

        <DataGrid.Column
          field={COLUMN_APPLICATIONS}
          type={'text'}
          headerName={'Applications'}
          width={200}
          getValue={(v) => v?.name}
        />

        <DataGrid.Column
          field={COLUMN_TRAFFIC_TYPE}
          type={'text'}
          headerName={'Traffic'}
          width={168}
          getValue={(v) => v?.name}
        />

        <DataGrid.Column
          field={COLUMN_ACTIVITY_TYPE}
          type={'text'}
          headerName={'Activity Type'}
          width={168}
          getValue={(v) => v?.name}
        />

        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          width={150}
          type={'text'}
          getValue={(v) => v?.name}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED}
          headerName={'Last Updated'}
          width={135}
          type={'date'}
          template={'LLL'}
        />

        <DataGrid.Column
          field={COLUMN_CREATED}
          headerName={'Created'}
          width={135}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          actionsComponent={IntegrationActions}
          onDuplicate={handleDuplicate}
          onEdit={handleEdit}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

function getIntegrationRowId(row) {
  return row.uniqueId;
}

export default IntegrationDataTable;
